

import React, { useState, useEffect } from 'react';
import { Grid, Card, Tooltip, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import GeneralSettings from 'components/SettingsComponents/GenralSettings';
import SettingsCard from 'components/SettingsComponents/SettingsCard';
import {
  CardMembership,
  InfoOutlined,
  FlightTakeoff,
  Close as CloseIcon
} from '@mui/icons-material';

import SettingsIcon from '@mui/icons-material/Settings';
import TravelSettings from 'components/SettingsComponents/TravelSettings';
import { Switch, FormControlLabel, Box, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PageHeader from 'components/v2/PageHeader';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import { Search as SearchIcon, X as ClearIcon } from 'lucide-react';
import { v2 as v2t } from 'styles/tokens/v2';
import { searchSettings } from './settingsSearch';
import useSettingsSections from './settings/useSettingsSections';
import { useSettingsQuery } from './settings/SettingsQueryContext';


function Settings() {
  const { axiosInstance } = useAuth();
  const isV2 = useTheme()?.pearlUi === 'v2';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get URL parameters

  const tabFromUrl = searchParams.get('tab');
  const objectIdFromUrl = searchParams.get('objectId');

  // State for which settings section is active
  const [activeSection, setActiveSection] = useState('Company Settings');
  const [showDialog, setShowDialog] = useState(false);
  const [dialogComponent, setDialogComponent] = useState(null);
  const [dialogTitle, setDialogTitle] = useState('');

  // State for company info
  const [companyInfo, setCompanyInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlayground, setIsPlayground] = useState(false);
  const [isTravelEnabled, setIsTravelEnabled] = useState(false);
  const [showTravelDialog, setShowTravelDialog] = useState(false);
  // v2 settings search: filters the cards and lists the areas inside each
  // card that match (see settingsSearch.js). Inside the v2 settings layout the
  // search box lives in the settings menu and shares its query here.
  const [localQuery, setLocalQuery] = useState('');
  const menuQuery = useSettingsQuery();
  const settingsQuery = menuQuery ? menuQuery.query : localQuery;
  const setSettingsQuery = menuQuery ? menuQuery.setQuery : setLocalQuery;
  // v2 shows a section in the page beside the settings menu instead of a
  // dialog; classic keeps the dialogs.
  const inline = isV2 && Boolean(menuQuery);

  // Fetch company info on component mount
  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  // Handle URL parameters
  useEffect(() => {
    if (tabFromUrl) {
      // In the page (v2) one section shows at a time: moving from one to
      // another through the menu closes the one before. Classic opens them
      // as dialogs, which are closed before another can be chosen.
      if (tabFromUrl === 'travel') {
        if (inline) setShowDialog(false);
        setShowTravelDialog(true);
      } else {
        if (inline) setShowTravelDialog(false);
        handleUrlNavigation(tabFromUrl, objectIdFromUrl);
      }
    } else if (inline) {
      // Leaving a section shown in the page (menu, breadcrumb or Back)
      // returns to the overview, as closing its dialog does in classic.
      setShowDialog(false);
      setShowTravelDialog(false);
    }
  }, [tabFromUrl, objectIdFromUrl]);

  const handleUrlNavigation = (tab, objectId) => {
    const sectionMap = {
      'company': 'Company Settings',
      'users': 'User Management',
      'licenses': 'Feature Licenses',
      'automation': 'Automation Builder',
      'layouts': 'Page Layouts',
      'sidebar': 'Sidebar Navigation',
      'integrations': 'Integrations',
      'custom-objects': 'Custom Objects',
      'support': 'Support Access',
      'webforms': 'Web Forms',
      'email-to-case': 'Email to Case',
      'payments': 'Payments',
      'website': 'Website',
      'telephony': 'Telephony',
      'seep-ai': 'SEEP AI',
      'ai-assistant': 'AI Assistant',
      'queues': 'Queues'
    };

    const sectionTitle = sectionMap[tab];
    if (sectionTitle) {
      const section = settingsSections.find(s => s.title === sectionTitle);
      // A locked card does not open from a deep link either, or ?tab= would be
      // a way round the lock the card itself shows.
      if (section && section.lockedReason) {
        setSearchParams({});
        return;
      }
      if (section && section.isNavigate && section.navigateTo) {
        setSearchParams({});
        navigate(section.navigateTo);
        return;
      }
      if (section && !section.isNavigate) {
        // Check if available in playground mode
        if (isPlayground && !isAvailableInPlayground(sectionTitle)) {
          // Clear URL params and show message
          setSearchParams({});
          return;
        }

        setActiveSection(sectionTitle);
        setDialogTitle(sectionTitle);

        // Set dialog component for the selected section
        setDialogComponent(section.component);
        // Open dialog
        setShowDialog(true);
      }
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setDialogComponent(null);
    setDialogTitle('');
    // Clear URL parameters
    setSearchParams({});
  };

  const fetchCompanyInfo = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/company/info');
      console.log('Company Info:', response.data);
      console.log('Is Playground:', response.data.is_playground);
      setCompanyInfo(response.data);
      setIsPlayground(response.data.is_playground === true);
      
      // Check if travel is enabled using boolean field
      // Handle both boolean and undefined/null cases
      const travelEnabled = response.data.is_travel_enabled === true || response.data.is_travel_enabled === 'true';
      setIsTravelEnabled(travelEnabled);
    } catch (error) {
      console.error('Error fetching company info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const {
    settingsSections,
    futureSections,
    isAvailableInPlayground,
  } = useSettingsSections();

  // Handle travel toggle
  const handleTravelToggle = async (event) => {
    const enabled = event.target.checked;
    const previousValue = isTravelEnabled;
    
    // Optimistically update UI
    setIsTravelEnabled(enabled);
    
    try {
      // Update company info with is_travel_enabled boolean
      const response = await axiosInstance.put('/company/info', {
        ...companyInfo,
        is_travel_enabled: enabled
      });
      
      // Update local company info state with the response
      const confirmedEnabled =
        response.data?.is_travel_enabled === true ||
        response.data?.is_travel_enabled === 'true' ||
        enabled;

      if (response.data) {
        setCompanyInfo(prev => ({
          ...prev,
          is_travel_enabled: confirmedEnabled
        }));
        setIsTravelEnabled(confirmedEnabled);
      } else {
        // If response doesn't have data, fetch fresh data
        await fetchCompanyInfo();
      }
      
      // Dispatch event so App/sidenav refresh immediately
      window.dispatchEvent(
        new CustomEvent('travelFeatureUpdated', { detail: { enabled: confirmedEnabled } })
      );
    } catch (error) {
      console.error('Error updating travel feature:', error);
      // Revert on error
      setIsTravelEnabled(previousValue);
    }
  };

  // Handle card click
  const handleCardClick = (section) => {
    if (section.isTravel) {
      // Open travel dialog
      setShowTravelDialog(true);
      setSearchParams({ tab: section.urlTab });
    } else if (section.isNavigate && section.navigateTo) {
      navigate(section.navigateTo);
    } else if (section.component) {
      // Open dialog immediately for non-navigating sections
      setDialogComponent(section.component);
      setDialogTitle(section.title);
      setShowDialog(true);
      // Set URL parameters
      setSearchParams({ tab: section.urlTab });
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h6">Loading settings...</MDTypography>
        </MDBox>
        
      </DashboardLayout>
    );
  }

  // Travel settings: the enable switch, then the settings or the disabled
  // note. In the full-screen dialog in classic, in the page in v2.
  const travelBody = (
    <>
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={isTravelEnabled}
              onChange={handleTravelToggle}
              color="primary"
            />
          }
          label={
            <MDBox>
              <MDTypography variant="body1" fontWeight="medium">
                Enable Travel Module
              </MDTypography>
              <MDTypography variant="caption" color="text" sx={{ display: 'block' }}>
                Enable this feature to access travel-related settings, trips, and publishers
              </MDTypography>
            </MDBox>
          }
        />
      </Box>
      <Divider sx={{ my: 2 }} />
      {isTravelEnabled ? (
        <TravelSettings />
      ) : (
        <MDBox 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          py={8}
          sx={{ height: 'calc(100% - 200px)' }}
        >
          <FlightTakeoff sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <MDTypography variant="h6" color="text.secondary" gutterBottom>
            Travel Module is Disabled
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary" textAlign="center">
            Enable the Travel Module above to access travel-related settings and features
          </MDTypography>
        </MDBox>
      )}
    </>
  );

  const searching = isV2 && settingsQuery.trim().length > 0;
  const searchResults = searchSettings([...settingsSections, ...futureSections], searching ? settingsQuery : '');
  const resultByTitle = new Map(searchResults.map((r) => [r.card.title, r]));
  const shownInSearch = (section) => !searching || resultByTitle.has(section.title);
  // An area with its own address opens it; any other area opens its card.
  const areaMatches = (section, isBlocked) =>
    searching && !isBlocked
      ? (resultByTitle.get(section.title)?.areas || []).map((area) => ({
          label: area.label,
          onClick: () => (area.to ? navigate(area.to) : handleCardClick(section)),
        }))
      : [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 3 : 6} pb={3}>
        {isV2 ? (
          // v2 (design-reference "Settings · Overview"): page title in place
          // of the gradient banner.
          <PageHeader
            title={inline && showDialog ? dialogTitle : inline && showTravelDialog ? 'Travel Settings' : 'Settings'}
            summary={inline && (showDialog || showTravelDialog) ? null : isPlayground
              ? 'Manage your account settings and preferences · Playground mode, limited settings available'
              : 'Manage your account settings and preferences'}
            actions={inline && (showDialog || showTravelDialog) ? (
              <Button variant="outlined" onClick={() => setSearchParams({})}>
                All settings
              </Button>
            ) : menuQuery ? null : (
              <TextField
                size="small"
                type="search"
                value={settingsQuery}
                onChange={(e) => setSettingsQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setSettingsQuery(''); }}
                placeholder="Search settings"
                inputProps={{ 'aria-label': 'Search settings', autoComplete: 'off' }}
                sx={{
                  width: { xs: '100%', sm: 300 },
                  '& input::-webkit-search-cancel-button': { display: 'none' },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={16} color={v2t.text3} aria-hidden="true" />
                    </InputAdornment>
                  ),
                  endAdornment: settingsQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={() => setSettingsQuery('')} sx={{ p: 0.25 }}>
                        <ClearIcon size={14} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}
          />
        ) : (
        <Card
          sx={{
            position: "relative",
            overflow: "visible",
            mb: 4,
            backgroundImage: "linear-gradient(to right, #1565C8, #5B9BD5)",
            boxShadow: "0 8px 20px -12px rgba(0, 158, 219, 0.6)"
          }}
        >
          <MDBox p={3}>
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="5rem"
                  height="5rem"
                  borderRadius="xl"
                  shadow="md"
                  bgColor="white"
                  color="info"
                >
                  <SettingsIcon sx={{ fontSize: 36 }} />
                </MDBox>
              </Grid>
              <Grid item xs>
                <MDBox ml={1}>
                  <MDTypography variant="h4" fontWeight="medium" color="white">
                    Settings
                  </MDTypography>
                  <MDTypography variant="body2" color="white" opacity={0.8}>
                    Manage your account settings and preferences
                  </MDTypography>
                  {isPlayground && (
                    <MDTypography variant="caption" color="white" opacity={0.9} sx={{ mt: 1, display: 'block' }}>
                      Playground Mode - Limited settings available
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
        )}

        {inline && showDialog && (
          <Card sx={{ p: 3 }}>{dialogComponent}</Card>
        )}
        {inline && showTravelDialog && <Card sx={{ p: 3 }}>{travelBody}</Card>}

        <Grid container spacing={isV2 ? 2 : 3} sx={inline && (showDialog || showTravelDialog) ? { display: 'none' } : undefined}>
          {/* Active Settings Sections */}
          {searching && searchResults.length === 0 && (
            <Grid item xs={12}>
              <MDTypography role="status" sx={{ py: 4, textAlign: 'center', fontSize: 14, color: v2t.text2 }}>
                No settings match “{settingsQuery.trim()}”. Try a section name such as “currency” or “phone numbers”.
              </MDTypography>
            </Grid>
          )}
          {settingsSections.filter(shownInSearch).map((section, index) => {
            // Determine if this setting should be disabled
            const isDisabled = isPlayground && !isAvailableInPlayground(section.title);
            // A card can also be locked because the tenant has not bought the
            // module it configures. Both must block the click: the wrapper div
            // below opens the card, and gating it on the playground flag alone
            // would let a locked card navigate anyway.
            const isBlocked = isDisabled || Boolean(section.lockedReason);

            return (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                lg={4}
                key={section.title}
                sx={{ 
                  display: 'flex',
                  minHeight: isV2 ? 0 : '280px'
                }}
              >
                <div 
                  onClick={() => !isBlocked && handleCardClick(section)}
                  style={{ 
                    width: '100%', 
                    display: 'flex',
                    minHeight: '100%'
                  }}
                >
                  <SettingsCard
                    title={section.title}
                    icon={section.icon}
                    description={section.description}
                    component={null} // Don't pass component, use dialog instead
                    isNavigate={!isBlocked && section.isNavigate}
                    navigateTo={!isBlocked ? section.navigateTo : ''}
                    disabled={isDisabled}
                    playgroundRestricted={isDisabled}
                    lockedReason={section.lockedReason || null}
                    matches={areaMatches(section, isBlocked)}
                  />
                </div>
              </Grid>
            );
          })}

          {/* Future/Coming Soon Sections */}
          {futureSections.filter(shownInSearch).map((section, index) => (
              <Grid 
              item 
              xs={12} 
              sm={6} 
              md={4} 
              lg={4}
              key={`future-${index}`}
              sx={{ 
                display: 'flex',
                minHeight: isV2 ? 0 : '280px'
              }}
            >
              <div style={{ 
                width: '100%', 
                display: 'flex',
                minHeight: '100%'
              }}>
                <SettingsCard
                  title={section.title}
                  icon={section.icon}
                  description={section.description}
                  comingSoon={true}
                />
              </div>
            </Grid>
          ))}
        </Grid>

        {/* Settings Dialog */}
        <Dialog
          open={!inline && showDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              height: '90vh',
              maxHeight: '900px'
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h5" fontWeight="medium">
                {dialogTitle}
              </MDTypography>
              <IconButton onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {dialogComponent}
          </DialogContent>
        </Dialog>

        {/* Travel Settings Dialog */}
        <Dialog
          open={!inline && showTravelDialog}
          onClose={() => {
            setShowTravelDialog(false);
            setSearchParams({});
          }}
          maxWidth={false}
          fullWidth
          fullScreen
          sx={{
            '& .MuiDialog-paper': {
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              margin: 0,
              borderRadius: 0
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h5" fontWeight="medium">
                Travel Settings
              </MDTypography>
              <IconButton onClick={() => {
                setShowTravelDialog(false);
                setSearchParams({});
              }}>
                <CloseIcon />
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent sx={{ p: 3, height: 'calc(100% - 64px)', overflow: 'auto' }}>
            {travelBody}
          </DialogContent>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default Settings;
